// @credit: 76a97b1cd4349534e19fb23cf80c4a0f1b1502418015de1e9d68b95c44bc36d433ed49b8
//
// Aphilaty PWA — App Shell (Topbar + Sidebar)

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth, OrgDoc } from '../context/AuthContext';
import { getDocument, subscribeToQuery, where, orderBy, limit } from '../services/firebase';

export default function AppShell() {
  const { user, userDoc, activeOrg, activeOrgId, isLoading,
          isAuthenticated, isPlatformAdmin, signOut, setActiveOrgId } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  const [allOrgs, setAllOrgs] = useState<Array<{ id: string; name: string }>>([]);

  // Redirect to login if not authenticated
  if (!isLoading && !isAuthenticated) {
    navigate('/login');
    return null;
  }

  // Org options for switcher — PWA-provisioned orgs only (memberType === 'pwa_admin'
  // excludes My Family and other starter orgs, which have memberType 'consumer')
  const adminOrgIds = userDoc?.orgMemberships
    ?.filter(m => ['admin', 'org_creator', 'org_approver'].includes(m.role) && m.memberType === 'pwa_admin')
    .map(m => m.orgId) ?? [];

  // Look up display names for the switcher — orgMemberships only carries IDs.
  // Skipped for platform admins, who switch via the full directory below instead.
  useEffect(() => {
    if (isPlatformAdmin) return;
    const missing = adminOrgIds.filter(id => !(id in orgNames));
    if (!missing.length) return;
    missing.forEach(id => {
      getDocument<OrgDoc>(`organizations/${id}`).then(org => {
        if (org) setOrgNames(prev => ({ ...prev, [id]: org.name }));
      });
    });
  }, [adminOrgIds.join(','), isPlatformAdmin]);

  // Platform admins aren't limited to orgs they belong to — they can act as a
  // backstop for any org, so the switcher draws from the full directory.
  useEffect(() => {
    if (!isPlatformAdmin) { setAllOrgs([]); return; }
    return subscribeToQuery<{ id: string; name: string; joinMode?: string; isStarterOrg?: boolean }>(
      'organizations',
      [where('isDeleted', '==', false), limit(200)],
      data => setAllOrgs(
        data.filter(o => o.joinMode !== 'user' && !o.isStarterOrg)
            .sort((a, b) => a.name.localeCompare(b.name))
      )
    );
  }, [isPlatformAdmin]);

  const switcherOptions = isPlatformAdmin
    ? allOrgs
    : adminOrgIds.map(id => ({ id, name: orgNames[id] ?? id }));

  const orgName = activeOrg?.name ?? 'Select Org';

  const wlBehavior = activeOrg?.whiteLabelConfig?.behavior;
  const wlBranding = activeOrg?.whiteLabelConfig?.branding;
  const isLockedOrg = !!wlBehavior?.lockedOrgId;
  const showAphilatyBranding = wlBehavior?.showAphilatyBranding ?? true;
  const appDisplayName = wlBranding?.appName ?? (showAphilatyBranding ? 'Aphilaty' : orgName);

  return (
    <div className="app-shell">
      {/* ── Topbar ── */}
      <header className="topbar" role="banner">
        <NavLink to="/dashboard" className="topbar-logo" aria-label="Aphilaty Admin Home">
          {wlBranding?.logoUrl ? (
            <img src={wlBranding.logoUrl} alt={appDisplayName}
              style={{ height: 24, maxWidth: 120, objectFit: 'contain', verticalAlign: 'middle' }} />
          ) : appDisplayName}
        </NavLink>

        <nav className="topbar-nav" aria-label="Main navigation">
          <NavLink to="/dashboard" className={({ isActive }) => isActive ? 'active' : ''}>
            Dashboard
          </NavLink>
          <NavLink to="/offers" className={({ isActive }) => isActive ? 'active' : ''}>
            Offers
          </NavLink>
          <NavLink to="/analytics" className={({ isActive }) => isActive ? 'active' : ''}>
            Analytics
          </NavLink>
          <NavLink to="/messages" className={({ isActive }) => isActive ? 'active' : ''}>
            Messages
          </NavLink>
          <NavLink to="/events" className={({ isActive }) => isActive ? 'active' : ''}>
            Events
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>
            Settings
          </NavLink>
          {isPlatformAdmin && (
            <NavLink to="/admin/platform" className={({ isActive }) => isActive ? 'active' : ''}>
              Platform Admin
            </NavLink>
          )}
        </nav>

        <div className="topbar-right">
          {switcherOptions.length >= 1 && !isLockedOrg ? (
            <select
              className="org-badge"
              value={activeOrgId ?? ''}
              onChange={e => setActiveOrgId(e.target.value)}
              aria-label="Switch organization"
              style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.12)',
                border: 'none', color: '#fff', padding: '2px 8px',
                borderRadius: '4px', fontSize: '11px' }}
            >
              {!activeOrgId && <option value="" style={{ color: '#111', background: '#fff' }}>Select Org</option>}
              {switcherOptions.map(o => (
                <option key={o.id} value={o.id} style={{ color: '#111', background: '#fff' }}>{o.name}</option>
              ))}
            </select>
          ) : (
            <span className="org-badge">{orgName}</span>
          )}

          <div style={{ position: 'relative' }}>
            <button
              className="user-menu-btn"
              onClick={() => setShowUserMenu(v => !v)}
              aria-label="User menu"
              aria-expanded={showUserMenu}
            >
              {user?.email?.split('@')[0] ?? 'User'} ▾
            </button>
            {showUserMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0,
                background: 'var(--color-bg)', border: '0.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', minWidth: 160, zIndex: 200,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              }}>
                <button onClick={() => { navigate('/settings'); setShowUserMenu(false); }}
                  style={{ display: 'block', width: '100%', padding: '9px 14px',
                    textAlign: 'left', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: '13px', color: 'var(--color-text)' }}>
                  Settings
                </button>
                <button onClick={() => { signOut(); setShowUserMenu(false); }}
                  style={{ display: 'block', width: '100%', padding: '9px 14px',
                    textAlign: 'left', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: '13px', color: 'var(--color-danger)' }}>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Sidebar ── */}
      <aside className="sidebar" aria-label="Section navigation">
        <SidebarContents />
      </aside>

      {/* ── Main ── */}
      <main className="main-content" id="main-content" tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}

function SidebarContents() {
  const { isPlatformAdmin } = useAuth();

  return (
    <>
      {/* Dashboard */}
      <div className="sidebar-section">Overview</div>
      <NavLink to="/dashboard" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        📊 Dashboard
      </NavLink>

      {/* Offers */}
      <div className="sidebar-section">Offers</div>
      <NavLink to="/offers/new" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ＋ Add Offer
      </NavLink>
      <NavLink to="/offers" end className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ◈ All Offers
      </NavLink>
      <NavLink to="/offers/drafts" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ◻ Drafts
      </NavLink>

      {/* Analytics */}
      <div className="sidebar-section">Reports</div>
      <NavLink to="/analytics/items" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ☰ Item Summary
      </NavLink>
      <NavLink to="/analytics/items/detail" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ⊟ Item Detail
      </NavLink>
      <NavLink to="/analytics/inventory" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        📦 Inventory
        <span className="badge badge-v2" style={{ marginLeft: 'auto' }}>V2</span>
      </NavLink>
      <NavLink to="/analytics/offers" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ◈ Offer Performance
      </NavLink>

      {/* Messages */}
      <div className="sidebar-section">Messaging</div>
      <NavLink to="/messages" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ◻ Messages
      </NavLink>
      <NavLink to="/events" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ◷ Events
      </NavLink>

      {/* Export */}
      <div className="sidebar-section">Export</div>
      <NavLink to="/analytics/export" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ↓ Export CSV
      </NavLink>

      {/* Settings */}
      <div className="sidebar-section">Admin</div>
      <NavLink to="/settings" className={({ isActive }) =>
        `sidebar-item${isActive ? ' active' : ''}`}>
        ⚙ Settings
      </NavLink>
      {isPlatformAdmin && (
        <NavLink to="/admin/platform" className={({ isActive }) =>
          `sidebar-item${isActive ? ' active' : ''}`}>
          🛡 Platform Admin
        </NavLink>
      )}
    </>
  );
}
